// `vuetify/styles` must load before any other `vuetify/*` import: when CSS layers are
// enabled ($layers: true), this is the file that establishes vuetify's internal cascade
// sub-layer order (reset, transitions, base, components, ...). If a component-level
// autoImport CSS module (e.g. VOverlay/VRipple, pulled in transitively by `createVuetify`
// or `vuetify/blueprints`) registers a `vuetify.components`-style sub-layer first, that
// sub-layer gets locked in ahead of `reset` in the global layer order, and `!important`
// aside, plain component-level overrides silently lose to Vuetify's own reset styles.
import 'vuetify/styles';
import { createVuetify } from 'vuetify';
import { md3 } from 'vuetify/blueprints';
import * as directives from 'vuetify/directives';
import { aliases } from 'vuetify/iconsets/mdi';
import '@console/theme/styles/vuetify.scss';
import { isDark } from '@console/theme/useThemes';
import DayJsAdapter from '@date-io/dayjs';
import '@mdi/font/css/materialdesignicons.min.css';
import deepmerge from 'deepmerge';
import { E2E } from '@console/constants/config';
import * as tokens from '@console/design-system/tokens/generated/tokens-resolved';
import { unit2number } from '@console/design-system/tokens/token-utils.ts';

let defaults: Record<string, any> = {
  VRow: {
    noGutters: true,
  },
};

defaults = {
  ...defaults,

  VBtn: {
    class: ['text-none'],
    variant: 'flat',
    rounded: 'md',
    color: undefined,
  },
  VBtnGroup: {
    rounded: 'md',
  },
  VDatePicker: {
    rounded: 'md',
  },
  VCard: {
    class: ['pa-0'],

    VCardSubtitle: {
      class: ['pb-6 px-6'],
    },
    VCardText: {
      class: ['px-6 pb-4 pt-0'],
    },
    VCardActions: {
      class: ['px-6 pb-4 pt-2 '],
    },
  },
  VChip: {
    rounded: 'sm',
  },
  VDialog: {
    // elevation: 0,
  },
  VInput: {
    color: 'primary',
  },
  VSelect: {
    color: 'primary',
  },
  VTextField: {
    color: 'primary',
  },
  VCombobox: {
    color: 'primary',
  },
  VAutocomplete: {
    color: 'primary',
  },
  VTextarea: {
    color: 'primary',
  },
  VMenu: {
    color: 'primary',
    elevation: 2,
    // Class: ['surface2', 'on-surface--text'],
    transition: 'slide-y-transition',
    VList: {
      class: ['surface2', 'text-on-surface'],
    },
  },
  VSheet: {},
  VSnackbar: {
    // elevation: 3,
    class: ['inverse-surface', 'text-on-inverse-surface'],
  },
  VTabs: {
    sliderColor: 'primary',
  },
  VTooltip: {
    class: ['inverse-surface', 'text-on-inverse-surface'],
    style: 'opacity: 1',
    location: 'top',
    interactive: true,
    maxWidth: 500,
  },
};

// disable transitions in CI environment to reduce snapshot flakiness
if (E2E) {
  defaults = deepmerge(defaults, {
    VSelect: {
      transition: false,
    },
    VCombobox: {
      transition: false,
    },
    VMenu: {
      transition: false,
    },
    VAutocomplete: {
      transition: false,
    },
  });
}

export function getVuetify() {
  return createVuetify({
    blueprint: md3,
    directives,

    date: {
      adapter: DayJsAdapter as any,
    },

    defaults: {
      global: {
        ripple: false,
      },

      ...defaults,
    },

    icons: {
      aliases: {
        ...aliases,
        dropdown: 'mdi-chevron-down',
        error: 'mdi-alert-outline',
        info: 'mdi-information-outline',
        warning: 'mdi-exclamation',
      },
    },
    theme: {
      defaultTheme: isDark() ? 'dark' : 'light',
      themes: {
        light: {
          dark: false,
          colors: tokens.light.color.brand,
        },
        dark: {
          dark: true,
          colors: tokens.dark.color.brand,
        },
      },
    },
    // Custom `useDisplay()` thresholds (the `v-breakpoint`/`mobile`/`smAndDown`/etc. reactive
    // state), distinct from `$grid-breakpoints` (which drives CSS grid/utility classes - see
    // uno.config.ts's `breakpoints`). `lg` is bumped past 1440 so a MacBook Pro 13" screen (1440
    // logical px, minus ~16px scrollbar) still reads as `md`, not `lg`.
    display: {
      thresholds: {
        xs: unit2number(tokens.light.display.sm),
        sm: unit2number(tokens.light.display.md),
        md: unit2number(tokens.light.display.lg),
        lg: unit2number(tokens.light.display.xl), // Handle 1440 screens (macbook pro 13inch) - 16 is scrollBarWidth
        xl: unit2number(tokens.light.display.xxl),
      },
    },
  });
}
